import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { addDays, addMonths, addWeeks, endOfWeek, format, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, StatusPill, EmptyState } from "@/components/common";
import { MonthGrid, monthRange } from "@/components/MonthGrid";
import { useMeta } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { isoOf, toDate, fmtDate, URGENCY } from "@/lib/brand";
import { todayIn } from "@/lib/dates";

export default function CalendarPage() {
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const today = todayIn(user?.prefs?.timezone);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(toDate(sp.get("date") || today));
  const [selected, setSelected] = useState(sp.get("date") || null);
  const [cat, setCat] = useState("");
  const [events, setEvents] = useState([]);
  const { meta } = useMeta();
  const nav = useNavigate();

  const range = useMemo(() => {
    if (view === "month") return monthRange(cursor);
    if (view === "week") return { start: startOfWeek(cursor), end: endOfWeek(cursor) };
    return { start: cursor, end: addDays(cursor, 90) };
  }, [view, cursor]);

  useEffect(() => {
    api.get("/calendar", { params: { start: isoOf(range.start), end: isoOf(range.end) } }).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, [range]);

  const shown = events.filter((e) => !cat || e.category_id === cat);
  const move = (dir) => setCursor(view === "month" ? addMonths(cursor, dir) : view === "week" ? addWeeks(cursor, dir) : addDays(cursor, 90 * dir));
  const pick = (iso) => { setSelected(iso === selected ? null : iso); setSp(iso === selected ? {} : { date: iso }); };
  const dayList = selected ? shown.filter((e) => e.date === selected) : null;
  const label = view === "month" ? format(cursor, "MMMM yyyy") : `${format(range.start, "MMM d")} – ${format(range.end, "MMM d, yyyy")}`;

  const agenda = (list) => {
    const groups = {};
    list.forEach((e) => (groups[e.date] = groups[e.date] || []).push(e));
    const keys = Object.keys(groups).sort();
    if (!keys.length) return <EmptyState title="No dates in this range" text="Try another range or add a reminder." />;
    return keys.map((k) => (
      <div key={k} className="mb-5">
        <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${k === today ? "text-[var(--pm-primary)]" : "text-[var(--pm-muted)]"}`}>{format(toDate(k), "EEEE, MMM d")}{k === today ? " · Today" : ""}</p>
        {groups[k].map((e) => (
          <Link key={e.reminder_id + k} to={`/reminders/${e.reminder_id}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F6F4FD] border border-[var(--pm-border)] mb-2" data-testid={`agenda-event-${e.reminder_id}-${k}`}>
            <span className="w-1.5 self-stretch rounded-full" style={{ background: URGENCY[e.urgency]?.dot }} />
            <span className="flex-1 min-w-0"><span className="block font-semibold text-sm truncate">{e.title}</span><span className="text-xs text-[var(--pm-muted)]">{e.category_name}{e.event_time ? ` · ${e.event_time}` : ""}</span></span>
            <StatusPill urgency={e.urgency} />
          </Link>
        ))}
      </div>
    ));
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendar" subtitle="See every date by month, week or as an agenda." actions={<Link to="/reminders/new" className="pm-btn pm-btn-primary"><Plus className="w-4 h-4" /> Add Reminder</Link>} />
      <div className="pm-card p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => move(-1)} aria-label="Previous" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => setCursor(toDate(today))} data-testid="cal-today">Today</button>
            <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => move(1)} aria-label="Next" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            <h2 className="font-bold text-lg ml-2" data-testid="cal-range-label">{label}</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <select className="pm-input !min-h-[36px] !w-auto text-sm" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="cal-category-filter">
              <option value="">All categories</option>{meta?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {["month", "week", "agenda"].map((v) => <button key={v} className="pm-tab capitalize" data-active={view === v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}>{v}</button>)}
          </div>
        </div>
        <div className={`grid gap-6 ${view === "month" ? "xl:grid-cols-[1fr_320px]" : ""}`}>
          <div className="overflow-x-auto">
            {view === "month" && <div><MonthGrid compact={window.innerWidth < 768} cursor={cursor} events={shown} selected={selected} today={today} onSelect={pick} onEventClick={(e) => nav(`/reminders/${e.reminder_id}`)} /></div>}
            {view === "week" && (
              <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 min-w-0" data-testid="week-view">
                {Array.from({ length: 7 }).map((_, i) => {
                  const d = addDays(range.start, i); const iso = isoOf(d); const evs = shown.filter((e) => e.date === iso);
                  return (
                    <div key={iso} className={`rounded-xl border p-2 min-h-[160px] ${iso === today ? "border-[var(--pm-primary)] bg-[#F7F5FF]" : "border-[var(--pm-border)]"}`}>
                      <p className="text-xs font-bold text-[var(--pm-muted)]">{format(d, "EEE d")}</p>
                      {evs.map((e) => <Link key={e.reminder_id} to={`/reminders/${e.reminder_id}`} className={`block mt-1.5 text-xs font-semibold rounded-md px-2 py-1 truncate ${URGENCY[e.urgency]?.cls}`}>{e.title}</Link>)}
                    </div>
                  );
                })}
              </div>
            )}
            {view === "agenda" && <div data-testid="agenda-view">{agenda(shown)}</div>}
          </div>
          {view === "month" && (
            <aside className="rounded-2xl bg-[#FAF9FF] border border-[var(--pm-border)] p-4" data-testid="cal-day-panel">
              <p className="font-bold mb-3">{selected ? fmtDate(selected, "EEEE, MMM d") : "This month"}</p>
              <div className="max-h-[520px] overflow-auto">{agenda(dayList || shown)}</div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
